'use client';

import { c as _c } from "react/compiler-runtime";
import './sac-controls.scss';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { ChipGroup } from '@sola/ark-core/molecules/chip-group/chip-group.tsx';
import type { RadioGroupProps } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import type { SelectItemProps } from '@sola/ark-core/molecules/select/select.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { SliderInput } from '@sola/ark-core/molecules/slider-input/slider-input.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { SacFilterProps } from '@sola/schema/lib/schema.ts';
import { TrackChildSearch, trackClick } from '@sola/tracking/components/datalayer.tsx';
import type { SacQueryParams } from '../../utils/query-params.sac.tsx';
import { useSacQueryParams } from '../../utils/query-params.sac.tsx';
import { getBaseSizeProps } from '../../utils/text-utils.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';
import { buildChipItems, buildCountryOptions, buildDayOptions, buildGenderOptions, buildMonthOptions, buildTrackingEvent, getSerializedSacUrl, useSacControlsState } from './sac-controls.logic.ts';
type SacControlsProps = SacFilterProps & {
  variant?: 'default' | 'stacked';
  /** Keep filters behind the Advanced Search disclosure at every breakpoint. */
  alwaysCollapseFilters?: boolean;
  /** When true, reads filters from URL params (dynamic). When false, uses static CMS props. */
  isDynamic?: boolean;
};

/* ─── Sub-components ─────────────────────────────────────────────────────────── */

interface SacControlsGridProps {
  className?: string;
  gender?: string;
  genderLabel: string;
  genderAriaLabel: string;
  genderOptions: RadioGroupProps['items'];
  onGenderChange: (v: React.ChangeEvent<HTMLInputElement>) => void;
  ageRange: [number, number];
  ageLabel: string;
  ageMin: number;
  ageMax: number;
  onAgeCommit: (payload: {
    newValue?: number[];
  }) => void;
  countryValue: string;
  countryLabel: string;
  countryOptions: SelectItemProps[];
  onCountryChange: (v: React.ChangeEvent<HTMLSelectElement>) => void;
}
const SacControlsGrid: React.FC<SacControlsGridProps> = t0 => {
  const $ = _c(26);
  const {
    className,
    gender,
    genderLabel,
    genderAriaLabel,
    genderOptions,
    onGenderChange,
    ageRange,
    ageLabel,
    ageMin,
    ageMax,
    onAgeCommit,
    countryValue,
    countryLabel,
    countryOptions,
    onCountryChange
  } = t0;
  const t1 = `SacControls__controls-container p-comfy-xl grid grid-cols-1 md:grid-cols-3 gap-comfy-lg ${className ?? ""}`;
  let t2;
  if ($[0] !== genderLabel) {
    t2 = <label id="gender-group-label" className="form-label label">{genderLabel}</label>;
    $[0] = genderLabel;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== gender || $[3] !== genderAriaLabel || $[4] !== genderOptions || $[5] !== onGenderChange) {
    t3 = <RadioGroup defaultValue={gender} aria-label={genderAriaLabel} loop={false} variant="buttons" name="gender" aria-labelledby="gender-group-label" onChange={onGenderChange} items={genderOptions} />;
    $[2] = gender;
    $[3] = genderAriaLabel;
    $[4] = genderOptions;
    $[5] = onGenderChange;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <span>{t2}{t3}</span>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== ageLabel || $[11] !== ageMax || $[12] !== ageMin || $[13] !== ageRange || $[14] !== onAgeCommit) {
    t5 = <span><SliderInput value={ageRange} label={ageLabel} min={ageMin} max={ageMax} name="age" showThumbLabels={true} onCommit={onAgeCommit} /></span>;
    $[10] = ageLabel;
    $[11] = ageMax;
    $[12] = ageMin;
    $[13] = ageRange;
    $[14] = onAgeCommit;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== countryLabel || $[17] !== countryOptions || $[18] !== countryValue || $[19] !== onCountryChange) {
    t6 = <span><Select value={countryValue} label={countryLabel} name="country" onChange={onCountryChange} items={countryOptions} /></span>;
    $[16] = countryLabel;
    $[17] = countryOptions;
    $[18] = countryValue;
    $[19] = onCountryChange;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t1 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <div className={t1}>{t4}{t5}{t6}</div>;
    $[21] = t1;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
};
interface BirthdayControlsProps {
  birthdayLabel: string;
  birthMonth?: number;
  birthMonthAriaLabel: string;
  monthOptions: SelectItemProps[];
  onBirthMonthChange: (v: React.ChangeEvent<HTMLSelectElement>) => void;
  birthDay?: number;
  birthDayAriaLabel: string;
  dayOptions: SelectItemProps[];
  onBirthDayChange: (v: React.ChangeEvent<HTMLSelectElement>) => void;
}

// TODO: add a reset-date button
const BirthdayControls: React.FC<BirthdayControlsProps> = t0 => {
  const $ = _c(19);
  const {
    birthdayLabel,
    birthMonth,
    birthMonthAriaLabel,
    monthOptions,
    onBirthMonthChange,
    birthDay,
    birthDayAriaLabel,
    dayOptions,
    onBirthDayChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ArkIcon iconName="gift-01" className="SacControls__birthday-icon hidden md:block" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== birthdayLabel) {
    t2 = <div className="label-md label-moderate">{birthdayLabel}</div>;
    $[1] = birthdayLabel;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== birthMonth || $[4] !== birthMonthAriaLabel || $[5] !== monthOptions || $[6] !== onBirthMonthChange) {
    t3 = <Select value={birthMonth} name="birth month" aria-label={birthMonthAriaLabel} onChange={onBirthMonthChange} items={monthOptions} />;
    $[3] = birthMonth;
    $[4] = birthMonthAriaLabel;
    $[5] = monthOptions;
    $[6] = onBirthMonthChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== birthDay || $[9] !== birthDayAriaLabel || $[10] !== dayOptions || $[11] !== onBirthDayChange) {
    t4 = <Select value={birthDay} name="birth day" aria-label={birthDayAriaLabel} onChange={onBirthDayChange} items={dayOptions} />;
    $[8] = birthDay;
    $[9] = birthDayAriaLabel;
    $[10] = dayOptions;
    $[11] = onBirthDayChange;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <span className="flex gap-comfy-sm">{t3}{t4}</span>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t2 || $[17] !== t5) {
    t6 = <div className="flex flex-col md:flex-row items-center justify-center p-comfy-lg gap-comfy-sm">{t1}{t2}{t5}</div>;
    $[16] = t2;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
interface AdvancedOptionsProps {
  showAdvanced: boolean;
  alwaysCollapseFilters?: boolean;
  advancedLabel: string;
  onToggle: () => void;
  children: React.ReactNode;
}
const AdvancedOptions: React.FC<AdvancedOptionsProps> = t0 => {
  const $ = _c(17);
  const {
    showAdvanced,
    alwaysCollapseFilters,
    advancedLabel,
    onToggle,
    children
  } = t0;
  const t1 = !alwaysCollapseFilters && "md:hidden";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("SacControls__adv-search-container flex flex-col p-comfy-xs md:p-comfy-sm justify-center", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = showAdvanced && children;
  let t4;
  if ($[2] !== showAdvanced) {
    t4 = showAdvanced ? <ArkIcon iconName="chevron-up" /> : <ArkIcon iconName="chevron-down" />;
    $[2] = showAdvanced;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== advancedLabel || $[5] !== onToggle) {
    t5 = () => {
      trackClick(advancedLabel);
      onToggle();
    };
    $[4] = advancedLabel;
    $[5] = onToggle;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== advancedLabel || $[8] !== t5) {
    t6 = <button type="button" onClick={t5}>{advancedLabel}</button>;
    $[7] = advancedLabel;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t6) {
    t7 = <ButtonStyle variant="tertiary" fullWidth={true} iconAfter={t4}>{t6}</ButtonStyle>;
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t7) {
    t8 = <div className={t2}>{t3}{t7}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
};

/* ─── Main component ─────────────────────────────────────────────────────────── */

const InternalSacControls: React.FC<SacControlsProps & {
  sacFilters?: SacQueryParams;
  setSacFilters?: (values: Partial<SacQueryParams>) => void;
}> = t0 => {
  const $ = _c(98);
  let alwaysCollapseFilters;
  let isDynamic;
  let props;
  let sacFilters;
  let setSacFilters;
  let t1;
  if ($[0] !== t0) {
    ({
      variant: t1,
      alwaysCollapseFilters,
      isDynamic,
      sacFilters,
      setSacFilters,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alwaysCollapseFilters;
    $[2] = isDynamic;
    $[3] = props;
    $[4] = sacFilters;
    $[5] = setSacFilters;
    $[6] = t1;
  } else {
    alwaysCollapseFilters = $[1];
    isDynamic = $[2];
    props = $[3];
    sacFilters = $[4];
    setSacFilters = $[5];
    t1 = $[6];
  }
  const variant = t1 === undefined ? "default" : t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["children", "core", "geo"];
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    t
  } = useTranslation(t2);
  const {
    showAdvanced,
    ageRange,
    isMultipleCountry,
    sacMinAge,
    sacMaxAge,
    handleGenderChange,
    handleAgeSliderCommit,
    handleCountryChange,
    handleBirthMonthChange,
    handleBirthDayChange,
    handleChipChange,
    toggleAdvanced
  } = useSacControlsState(sacFilters, setSacFilters);
  const t3 = sacFilters?.filters;
  let t4;
  if ($[8] !== t || $[9] !== t3) {
    t4 = buildChipItems(t, t3);
    $[8] = t;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const chips = t4;
  let t5;
  if ($[11] !== t) {
    t5 = buildGenderOptions(t);
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const genderOptions = t5;
  let t6;
  if ($[13] !== isMultipleCountry || $[14] !== t) {
    t6 = buildCountryOptions(t, isMultipleCountry);
    $[13] = isMultipleCountry;
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const countryOptions = t6;
  let t7;
  if ($[16] !== t) {
    t7 = buildMonthOptions(t);
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const monthOptions = t7;
  let t8;
  if ($[18] !== t) {
    t8 = buildDayOptions(t);
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const dayOptions = t8;
  let t9;
  if ($[20] !== sacFilters) {
    t9 = buildTrackingEvent(sacFilters);
    $[20] = sacFilters;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const trackingEvent = t9;
  const showChips = variant !== "stacked";
  const t10 = sacFilters?.gender;
  let t11;
  if ($[22] !== t) {
    t11 = t("children:sac.wantToSponsor");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t) {
    t12 = t("children:sac.controls.chooseGender");
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("children:sac.betweenTheAges");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = isMultipleCountry ? "multiple" : sacFilters?.countries?.[0] ?? "";
  let t15;
  if ($[28] !== t) {
    t15 = t("children:sac.whoLivesIn");
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== ageRange || $[31] !== countryOptions || $[32] !== genderOptions || $[33] !== handleAgeSliderCommit || $[34] !== handleCountryChange || $[35] !== handleGenderChange || $[36] !== sacMaxAge || $[37] !== sacMinAge || $[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== t14 || $[43] !== t15) {
    t16 = {
      gender: t10,
      genderLabel: t11,
      genderAriaLabel: t12,
      genderOptions,
      onGenderChange: handleGenderChange,
      ageRange,
      ageLabel: t13,
      ageMin: sacMinAge,
      ageMax: sacMaxAge,
      onAgeCommit: handleAgeSliderCommit,
      countryValue: t14,
      countryLabel: t15,
      countryOptions,
      onCountryChange: handleCountryChange
    };
    $[30] = ageRange;
    $[31] = countryOptions;
    $[32] = genderOptions;
    $[33] = handleAgeSliderCommit;
    $[34] = handleCountryChange;
    $[35] = handleGenderChange;
    $[36] = sacMaxAge;
    $[37] = sacMinAge;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const gridProps = t16;
  let t17;
  if ($[45] !== t) {
    t17 = t("children:sac.birthdaySearch");
    $[45] = t;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const t18 = sacFilters?.birthMonth;
  let t19;
  if ($[47] !== t) {
    t19 = t("children:sac.controls.month");
    $[47] = t;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const t20 = sacFilters?.birthDay;
  let t21;
  if ($[49] !== t) {
    t21 = t("children:sac.controls.day");
    $[49] = t;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== dayOptions || $[52] !== handleBirthDayChange || $[53] !== handleBirthMonthChange || $[54] !== monthOptions || $[55] !== t17 || $[56] !== t18 || $[57] !== t19 || $[58] !== t20 || $[59] !== t21) {
    t22 = {
      birthdayLabel: t17,
      birthMonth: t18,
      birthMonthAriaLabel: t19,
      monthOptions,
      onBirthMonthChange: handleBirthMonthChange,
      birthDay: t20,
      birthDayAriaLabel: t21,
      dayOptions,
      onBirthDayChange: handleBirthDayChange
    };
    $[51] = dayOptions;
    $[52] = handleBirthDayChange;
    $[53] = handleBirthMonthChange;
    $[54] = monthOptions;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  const birthdayProps = t22;
  const t23 = `SacControls SacControls--${variant} flex flex-col gap-roomy-xs`;
  let t24;
  if ($[61] !== t23) {
    t24 = cn(t23);
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== t) {
    t25 = t("children:sac.controls.legend");
    $[63] = t;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== trackingEvent) {
    t26 = <TrackChildSearch event={trackingEvent} />;
    $[65] = trackingEvent;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== alwaysCollapseFilters || $[68] !== birthdayProps || $[69] !== gridProps || $[70] !== showAdvanced || $[71] !== t || $[72] !== toggleAdvanced || $[73] !== variant) {
    t27 = variant !== "stacked" && <><SacControlsGrid {...gridProps} className={cn("hidden", !alwaysCollapseFilters && "md:grid")} /><BirthdayControls {...birthdayProps} /><AdvancedOptions showAdvanced={showAdvanced} alwaysCollapseFilters={alwaysCollapseFilters} advancedLabel={t("children:sac.advancedSearch")} onToggle={toggleAdvanced}><SacControlsGrid {...gridProps} /></AdvancedOptions></>;
    $[67] = alwaysCollapseFilters;
    $[68] = birthdayProps;
    $[69] = gridProps;
    $[70] = showAdvanced;
    $[71] = t;
    $[72] = toggleAdvanced;
    $[73] = variant;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== gridProps || $[76] !== props || $[77] !== variant) {
    t28 = variant === "stacked" && <><FeatureBase {...props} {...getBaseSizeProps(2)} {...{
        primaryCTA: undefined,
        secondaryCTA: undefined,
        tertiaryCTA: undefined
      }} className="p-comfy-xl pb-none" inline={true} /><SacControlsGrid {...gridProps} className="flex flex-col" /></>;
    $[75] = gridProps;
    $[76] = props;
    $[77] = variant;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  let t29;
  if ($[79] !== isDynamic || $[80] !== sacFilters || $[81] !== t) {
    t29 = !isDynamic && <div className="p-comfy-xl"><ButtonStyle variant="primary"><Link href={getSerializedSacUrl(sacFilters)}>{t("core:actions.sponsorAChild")}</Link></ButtonStyle></div>;
    $[79] = isDynamic;
    $[80] = sacFilters;
    $[81] = t;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  let t30;
  if ($[83] !== t27 || $[84] !== t28 || $[85] !== t29) {
    t30 = <div className="SacControls__card flex flex-col">{t27}{t28}{t29}</div>;
    $[83] = t27;
    $[84] = t28;
    $[85] = t29;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== chips || $[88] !== handleChipChange || $[89] !== sacFilters?.filters || $[90] !== showChips) {
    t31 = showChips && <ChipGroup chips={chips} selectedValues={sacFilters?.filters} handleSelectionChange={handleChipChange} isScrollable={true} />;
    $[87] = chips;
    $[88] = handleChipChange;
    $[89] = sacFilters?.filters;
    $[90] = showChips;
    $[91] = t31;
  } else {
    t31 = $[91];
  }
  let t32;
  if ($[92] !== t24 || $[93] !== t25 || $[94] !== t26 || $[95] !== t30 || $[96] !== t31) {
    t32 = <div className={t24} aria-label={t25} role="group">{t26}{t30}{t31}</div>;
    $[92] = t24;
    $[93] = t25;
    $[94] = t26;
    $[95] = t30;
    $[96] = t31;
    $[97] = t32;
  } else {
    t32 = $[97];
  }
  return t32;
};
export const LiveSacControls: React.FC<SacControlsProps> = props => {
  const $ = _c(17);
  const {
    isDynamic,
    gender,
    countries,
    ageMin,
    ageMax,
    disability,
    hiv,
    orphan,
    highlyVulnerable
  } = props;
  const hasDefaults = isDynamic && (gender !== undefined || countries !== undefined || ageMin !== undefined || ageMax !== undefined || disability !== undefined || hiv !== undefined || orphan !== undefined || highlyVulnerable !== undefined);
  let t0;
  if ($[0] !== ageMax || $[1] !== ageMin || $[2] !== countries || $[3] !== disability || $[4] !== gender || $[5] !== hasDefaults || $[6] !== highlyVulnerable || $[7] !== hiv || $[8] !== orphan) {
    t0 = hasDefaults ? {
      gender: gender as ChildGenderEnum | undefined,
      countries,
      ageMin,
      ageMax,
      disability,
      hiv,
      orphan,
      highlyVulnerable
    } : undefined;
    $[0] = ageMax;
    $[1] = ageMin;
    $[2] = countries;
    $[3] = disability;
    $[4] = gender;
    $[5] = hasDefaults;
    $[6] = highlyVulnerable;
    $[7] = hiv;
    $[8] = orphan;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  let t1;
  if ($[10] !== isDynamic || $[11] !== t0) {
    t1 = {
      isDynamic,
      defaults: t0
    };
    $[10] = isDynamic;
    $[11] = t0;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const {
    sacFilters,
    setSacFilters
  } = useSacQueryParams(t1);
  let t2;
  if ($[13] !== props || $[14] !== sacFilters || $[15] !== setSacFilters) {
    t2 = <InternalSacControls {...props} sacFilters={sacFilters} setSacFilters={setSacFilters} />;
    $[13] = props;
    $[14] = sacFilters;
    $[15] = setSacFilters;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
};

/** SSG fallback renders without search params; Suspense swaps in the live version. */
export const SacControls: React.FC<SacControlsProps> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={<InternalSacControls {...props} />}><LiveSacControls {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};